import {
  ChangePasswordDto,
  ForgotPasswordDto,
  ForgotPasswordResponse,
  LoginDto,
  LoginResponse,
  NotificationEntity,
  PhoneDto,
  ResendCodeDto,
  ResetPasswordDto,
  SendSmsDto,
  SetPasswordDto,
  UpdateUserDto,
  UpdateUserResponse,
  UserDataResponse,
  UserLite,
  VerifySmsDto,
  VerifySmsResponse,
} from '@/api';
import { checkResponse } from '@/shared/api/contract/responseContract';
import API from '@api/API';
import { ENDPOINTS } from '../endpoints';
import { userResponseSchema } from '@/shared/api/contract/user.schema';

export interface UpdateUserPayload extends Omit<UpdateUserDto, 'logo'> {
  logo?: File | string;
  logoURL?: string;
}

/**
 * Creates an API instance with a custom token for authenticated requests
 * Used for operations that require a temporary token (e.g., SMS verification)
 */
const createAuthenticatedApi = (token: string) => {
  // eslint-disable-next-line
  return API(token as any);
};

export const userApi = {
  login: async (data: LoginDto): Promise<LoginResponse> => {
    const response = await API().post(ENDPOINTS.user.login(), data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await API().post(ENDPOINTS.user.logout());
  },

  getUser: async (): Promise<UserDataResponse> => {
    const response = await API().get(ENDPOINTS.user.user());
    return checkResponse(userResponseSchema, response.data, 'GET /user');
  },

  updateUser: async (data: Partial<UpdateUserPayload>): Promise<UpdateUserResponse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'logo' && data.logoURL && value instanceof File) {
        formData.append('file', value);
      } else if (key === 'logo' && !data.logoURL && !value) {
        formData.append(key, 'undefined');
      } else if (key === 'logoURL') {
        // Skip logoURL - it's only used for preview
        return;
      } else if (typeof value === 'boolean') {
        formData.append(key, String(value));
      } else if (value != null && value !== '') {
        formData.append(key, String(value));
      }
    });

    const response = await API().post(ENDPOINTS.user.user(), formData);
    return response.data;
  },

  getAllUsers: async (): Promise<Array<UserLite>> => {
    const response = await API().get(ENDPOINTS.user.getAllUsers());
    return response.data;
  },

  resendCode: async (data: ResendCodeDto): Promise<void> => {
    const response = await API().post(ENDPOINTS.user.resendCode(), data);
    return response.data;
  },

  phone: async (data: PhoneDto): Promise<void> => {
    const response = await API().post(ENDPOINTS.user.phone(), data);
    return response.data;
  },

  verifyUserIdentity: async (): Promise<{ signatoryStatus: string; voucher: string }> => {
    const response = await API().post(ENDPOINTS.user.verifyIdentity());
    return response.data;
  },

  forgotPasswordEmail: async (data: ForgotPasswordDto): Promise<ForgotPasswordResponse> => {
    const response = await API().post(ENDPOINTS.user.forgotPasswordEmail(), data);
    return response.data;
  },

  /**
   * Verify SMS code with a temporary token
   * @param data - The verification code
   * @param token - Temporary auth token for SMS verification flow
   */
  verifySms: async (data: VerifySmsDto, token?: string): Promise<VerifySmsResponse> => {
    const apiInstance = token ? createAuthenticatedApi(token) : API();
    const response = await apiInstance.post(ENDPOINTS.user.verifySms(), data);
    return response.data;
  },

  /**
   * Send SMS verification code
   * @param data - Phone number to send SMS to
   * @param token - Temporary auth token for SMS verification flow
   */
  sendSms: async (data: SendSmsDto, token?: string): Promise<void> => {
    const apiInstance = token ? createAuthenticatedApi(token) : API();
    const response = await apiInstance.post(ENDPOINTS.user.sendSms(), data);
    return response.data;
  },

  confirmIndependence: async ({ auditId }: { auditId: number }) => {
    const response = await API().post(ENDPOINTS.audits.confirmIndependence(auditId));
    return response.data;
  },

  rejectIndependence: async ({ auditId }: { auditId: number }) => {
    const response = await API().post(ENDPOINTS.audits.rejectIndependence(auditId));
    return response.data;
  },

  changePassword: async (data: ChangePasswordDto): Promise<{ message: string }> => {
    const response = await API().post(ENDPOINTS.user.changePassword(), data);
    return response.data;
  },

  setPassword: async (data: SetPasswordDto, token: string): Promise<{ token?: string }> => {
    // @ts-ignore
    const response = await API(token).post(ENDPOINTS.user.setPassword(), data);
    return response.data;
  },

  /**
   * Send the reset 2FA code to the user's registered (pinned) phone.
   * @param token - The reset token from the email link
   */
  resetSendSms: async (token: string): Promise<{ sent?: boolean }> => {
    // @ts-ignore
    const response = await API(token).post(ENDPOINTS.user.resetSendSms());
    return response.data;
  },

  /**
   * Verify the reset SMS code without consuming it (gates the password step).
   * @param data - The SMS code received on the phone
   * @param token - The reset token from the email link
   */
  resetVerifySms: async (
    data: { code: string },
    token: string,
  ): Promise<{ verified?: boolean }> => {
    // @ts-ignore
    const response = await API(token).post(ENDPOINTS.user.resetVerifySms(), data);
    return response.data;
  },

  /**
   * Commit a new password during reset, gated on the SMS code.
   * Because the user passed email + SMS 2FA, the backend returns a full auth
   * payload (session token + user data) so we can sign them in directly.
   * @param data - The new password and the SMS code received on the phone
   * @param token - The reset token from the email link
   */
  resetPassword: async (data: ResetPasswordDto, token: string): Promise<VerifySmsResponse> => {
    // @ts-ignore
    const response = await API(token).post(ENDPOINTS.user.resetPassword(), data);
    return response.data;
  },

  // @ts-ignore
  isUserActive: async (token: string) => {
    try {
      const response = await createAuthenticatedApi(token).get(ENDPOINTS.user.isActive());
      return response.data;
    } catch (error) {
      return error;
    }
  },

  getNotifications: async ({
    type = 'important',
  }: {
    type?: 'general' | 'archive' | 'important' | string;
  }): Promise<NotificationEntity[]> => {
    const response = await API().get(ENDPOINTS.user.notifications(type));
    return response.data.notifications;
  },

  readGeneralNotifications: async (): Promise<{ success: boolean }> => {
    const response = await API().put(ENDPOINTS.user.readGeneralNotifications());
    return response.data;
  },

  readImportantNotifications: async (notificationId: number): Promise<{ success: boolean }> => {
    const response = await API().put(ENDPOINTS.user.readImportantNotifications(), {
      notificationId,
    });
    return response.data;
  },

  uploadSignature: async (signature: any): Promise<any> => {
    const FD = new FormData();
    FD.append('signature', signature);

    const response = await API().post(ENDPOINTS.user.signature(), FD);
    return response.data;
  },

  getSignature: async (): Promise<any> => {
    const response = await API().get(ENDPOINTS.user.signature());
    return response.data;
  },
};
